import { fetchConfig } from "../helpers/fetch";

export default class GiftRecipientForm extends HTMLElement {
  constructor() {
    super();

    this.abortController = new AbortController();

    const self = this;
    this.state = new Proxy(
      {
        loading: false,
        error: "",
        sendAsGift: true,
      },
      {
        set(state, key, value) {
          const oldValue = state[key];

          state[key] = value;
          if (oldValue !== value) {
            switch (key) {
              case "loading":
                self.processLoadingChange();
                break;
              case "error":
                self.processErrorChange();
                break;
              case "sendAsGift":
                self.processSendAsGiftChange();
                break;
              default:
                break;
            }
          }
          return state;
        },
      },
    );
  }

  get form() {
    return this.querySelector("form");
  }

  get submit() {
    return this.querySelector("[type='submit']");
  }

  get output() {
    return this.querySelector("output > span");
  }

  get sendAsGiftCheckbox() {
    return this.querySelector("input[name='send_as_gift']");
  }

  get shopifySendToRecipientInput() {
    return this.querySelector(
      "input[name='properties[__shopify_send_gift_card_to_recipient]']",
    );
  }

  get recipientInputs() {
    return this.querySelectorAll(
      ":is(input,textarea):not([type='hidden']):not([name='send_as_gift'])",
    );
  }

  get loadingOverlay() {
    return this.querySelector(".c-loading-overlay");
  }

  get cartDrawerComponent() {
    return document.getElementById("cart-snippet");
  }

  connectedCallback() {
    this.form.addEventListener("submit", this.handleSubmit, {
      signal: this.abortController.signal,
    });

    if (this.sendAsGiftCheckbox) {
      this.sendAsGiftCheckbox.addEventListener(
        "change",
        this.handleSendAsGiftChange,
        {
          signal: this.abortController.signal,
        },
      );
    }
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  handleSubmit = (event) => {
    event.preventDefault();

    const valid = this.form.reportValidity();

    if (!valid) return;

    this.state.loading = true;

    const formData = new FormData(event.target);

    const variantId = formData.get("variant");

    const properties = {};

    if (this.state.sendAsGift) {
      for (const [key, value] of formData) {
        const match = /properties\[(?<property>.+)\]/.exec(key);
        const property = match?.groups.property;

        if (property && !!value) {
          properties[property] = value;
        }
      }
    }

    const body = JSON.stringify({
      items: [
        {
          id: variantId,
          quantity: 1,
          properties,
        },
      ],
      sections: this.cartDrawerComponent.sectionsToRender.map(
        (section) => section.section,
      ),
    });

    fetch(`${routes.cart_add_url}.js`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        if (response.status) {
          this.state.error = response.description;
          return;
        }

        this.state.error = "";
        this.cartDrawerComponent.updateSectionHTML(response);
        this.cartDrawerComponent.toggleEmptyClass(response.items.length);

        postMessage({
          drawerId: "cart-drawer",
          drawerAction: "open",
        });

        const event = new CustomEvent("dialog:close", {
          bubbles: true,
          cancelable: true,
        });
        this.dispatchEvent(event);

        this.form.reset();
        this.state.sendAsGift = true;
      })
      .catch((error) => {
        console.error(error);
        this.state.error = "There was an error submitting the form.";
      })
      .finally(() => {
        this.state.loading = false;
      });
  };

  handleSendAsGiftChange = ({ target: { checked } }) => {
    this.state.sendAsGift = checked;
  };

  processLoadingChange() {
    this.submit.toggleAttribute("aria-disabled", this.state.loading);
    this.submit.classList.toggle("loading", this.state.loading);
    this.loadingOverlay.classList.toggle("hidden", !this.state.loading);
  }

  processErrorChange() {
    this.output.textContent = this.state.error;
  }

  processSendAsGiftChange() {
    for (const input of this.recipientInputs) {
      input.disabled = !this.state.sendAsGift;
    }
  }
}
